import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  ArrowUpRight,
  Stethoscope,
  ShieldCheck,
  Lock,
  Landmark,
  Award,
  HeartPulse,
  Hospital,
  Users,
  Workflow,
  FlaskConical,
  Activity,
  MapPin,
  Network,
  Mail,
  Phone,
  Linkedin,
} from 'lucide-react';
import Reveal from '@/components/Reveal';
import ContactForm from '@/components/ContactForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FOUNDER_IMG =
  'https://customer-assets.emergentagent.com/job_6fe4af30-54a6-49c2-888a-b800c65f893f/artifacts/cobplphu_IMG_8450.JPG';

// ─── Static section data ───────────────────────────────────────────────────────
const credibilityItems = [
  { icon: Stethoscope, label: 'Medical Doctor' },
  { icon: ShieldCheck, label: 'CDC Contract — VAERS Clinician' },
  { icon: Lock, label: 'Public Trust Level 5 Clearance' },
  { icon: Landmark, label: 'Municipal Advisor — City of Flint' },
  { icon: Award, label: 'Founder, Vantaris Integrated Solutions' },
];

const credentials = [
  'Medical Doctor',
  'VAERS Clinician — CDC Contract (Lukos)',
  'Public Trust Level 5 Security Clearance',
  'Municipal Healthcare Advisor — City of Flint, MI',
  'Founder, Vantaris Integrated Solutions',
];

const services = [
  {
    icon: Hospital,
    title: 'Healthcare Systems Advisory',
    description:
      'Strategic consultation for health systems, hospitals, and healthcare organizations navigating operational transformation, structural challenges, and population health strategy.',
  },
  {
    icon: HeartPulse,
    title: 'Public Health Strategy',
    description:
      'Population health planning, community health assessments, chronic disease strategy, preventive care frameworks, and public health infrastructure support for organizations and agencies.',
  },
  {
    icon: Landmark,
    title: 'Municipal & Government Health Advisory',
    description:
      'Physician-led advisory for city governments, public agencies, and cross-sector partnerships focused on improving community health outcomes and coordinating public health resources.',
  },
  {
    icon: Users,
    title: 'Health Equity & Community Strategy',
    description:
      'Strategic planning for underserved and urban communities facing healthcare disparities — with a focus on sustainable, systems-level change rooted in community realities.',
  },
  {
    icon: Workflow,
    title: 'Healthcare Operations Consulting',
    description:
      'Operational analysis, workflow design, implementation strategy, and performance improvement for healthcare organizations seeking to operate more effectively and efficiently.',
  },
  {
    icon: FlaskConical,
    title: 'Pharmacovigilance & Regulatory Advisory',
    description:
      'Clinical review, adverse event analysis, and regulatory-adjacent healthcare strategy informed by active federal contract experience and pharmacovigilance expertise.',
  },
];

const whyVantaris = [
  {
    icon: Stethoscope,
    title: 'Physician-Led Perspective',
    description:
      'Every engagement is grounded in real clinical training and systems-level medical insight. This isn\'t theory — it\'s the operational understanding that only comes from medical training and active practice.',
  },
  {
    icon: Activity,
    title: 'Public Health Informed',
    description:
      'Our strategy is shaped by population health data, community health realities, and a deep understanding of public health infrastructure — not just business frameworks.',
  },
  {
    icon: MapPin,
    title: 'Community Rooted',
    description:
      'Based in Flint, Michigan, we understand what healthcare gaps look like on the ground in underserved communities. That perspective shapes everything we do.',
  },
  {
    icon: Network,
    title: 'Cross-Sector Capable',
    description:
      'Equally fluent in clinical medicine, government advisory, healthcare operations, and community strategy — Vantaris operates across sectors where most advisors only operate in one.',
  },
];

const expertiseTags = [
  'Healthcare Systems Strategy',
  'Public Health Advisory',
  'Population Health Management',
  'Municipal Health Consulting',
  'Health Equity',
  'Pharmacovigilance',
  'Preventive Care Strategy',
  'Cross-Sector Partnerships',
  'Healthcare Operations',
  'Community Health Strategy',
  'Federal Healthcare Frameworks',
  'Chronic Disease Management',
  'Health Policy Implementation',
  'Healthcare Innovation',
  'Physician Executive Leadership',
];

// ─── Reusable section heading ──────────────────────────────────────────────────
const SectionHead = ({ eyebrow, title, subtitle, dark = true, align = 'left' }) => (
  <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''}`}>
    {eyebrow && (
      <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#D4AF37] mb-5">
        {eyebrow}
      </p>
    )}
    <h2
      className={`font-display text-3xl md:text-4xl lg:text-[2.75rem] font-semibold leading-[1.15] mb-5 ${
        dark ? 'text-white' : 'text-[#1B0E33]'
      }`}
    >
      {title}
    </h2>
    <div className={`vs-rule ${align === 'center' ? 'mx-auto' : ''} mb-6`} />
    {subtitle && (
      <p
        className={`text-base md:text-lg leading-relaxed ${
          dark ? 'text-slate-300' : 'text-slate-600'
        }`}
      >
        {subtitle}
      </p>
    )}
  </div>
);

const HomePage = () => {
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const res = await axios.get(`${API}/insights?limit=3`);
        setInsights(res.data);
      } catch (err) {
        console.error('Failed to load insights', err);
      }
    };
    fetchInsights();
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#1B0E33]">
      {/* ═══════════════════════ HERO ═══════════════════════ */}
      <section
        id="hero"
        data-testid="hero-section"
        className="relative min-h-screen flex items-center overflow-hidden hero-bg-texture"
      >
        <div className="absolute inset-0 hero-grid-overlay" aria-hidden="true" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-32 lg:py-40 w-full">
          <div className="max-w-4xl">
            <p
              data-testid="hero-eyebrow"
              className="text-[11px] md:text-xs font-medium uppercase tracking-[0.28em] text-[#D4AF37] mb-7 animate-fade-in-up"
            >
              Vantaris Integrated Solutions
            </p>
            <h1
              data-testid="hero-headline"
              className="font-display text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[4.5rem] font-semibold text-white leading-[1.05] mb-7 animate-fade-in-up animation-delay-100"
            >
              Bridging Clinical Medicine, Public Health, and{' '}
              <span className="text-gold-grad italic">Community Strategy.</span>
            </h1>
            <p
              data-testid="hero-subheading"
              className="text-base md:text-lg lg:text-xl text-slate-300 leading-relaxed mb-10 max-w-2xl animate-fade-in-up animation-delay-200"
            >
              Physician-led advisory at the intersection of healthcare systems, public health, government, and community impact.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
              <a href="#contact">
                <Button
                  data-testid="hero-cta-primary"
                  className="bg-[#D4AF37] text-[#1B0E33] hover:bg-[#E5C75B] px-8 py-4 h-auto rounded-sm text-[12px] font-semibold uppercase tracking-[0.12em] w-full sm:w-auto transition-all"
                >
                  Schedule a Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="#about">
                <Button
                  data-testid="hero-cta-secondary"
                  variant="outline"
                  className="border-white/25 bg-transparent text-white hover:bg-white/5 hover:border-[#D4AF37] hover:text-[#E5C75B] px-8 py-4 h-auto rounded-sm text-[12px] font-semibold uppercase tracking-[0.12em] w-full sm:w-auto transition-all"
                >
                  Learn More
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ CREDIBILITY BAR ═══════════════ */}
      <section
        id="credibility"
        data-testid="credibility-bar"
        className="bg-[#130723] border-y border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-7">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-4">
            {credibilityItems.map((item, i) => (
              <div
                key={item.label}
                data-testid={`credibility-item-${i}`}
                className="flex items-center gap-3 justify-center md:justify-start md:border-r last:border-r-0 md:border-white/5 md:pr-4"
              >
                <item.icon className="w-5 h-5 text-[#D4AF37] shrink-0" />
                <span className="text-[12px] md:text-[13px] font-medium text-slate-200 leading-tight">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ ABOUT DR. GILL ═══════════════ */}
      <section
        id="about"
        data-testid="about-section"
        className="bg-[#1B0E33] py-24 md:py-32 scroll-mt-20"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            {/* Photo */}
            <Reveal className="lg:col-span-5">
              <div className="relative max-w-[440px]">
                <div className="absolute -top-5 -left-5 w-full h-full border-2 border-[#D4AF37]/60 pointer-events-none" />
                <div className="relative aspect-[4/5] overflow-hidden bg-[#221141]">
                  <img
                    src={FOUNDER_IMG}
                    alt="Dr. Javon Gill, MD"
                    className="w-full h-full object-cover"
                    data-testid="about-founder-image"
                  />
                </div>
              </div>
            </Reveal>

            {/* Bio */}
            <Reveal delay={120} className="lg:col-span-7">
              <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#D4AF37] mb-5">
                About
              </p>
              <h2 className="font-display text-3xl md:text-4xl lg:text-[2.75rem] font-semibold text-white leading-[1.15] mb-2">
                Dr. Javon Gill, MD
              </h2>
              <p className="text-[#E5C75B] text-sm md:text-base tracking-wide mb-6">
                Physician · Healthcare Strategist · Public Health Advisor · Founder
              </p>
              <div className="vs-rule mb-7" />
              <div className="space-y-5 text-slate-300 text-base md:text-lg leading-relaxed">
                <p>
                  Dr. Javon Gill is a Medical Doctor and healthcare strategist based in Flint, Michigan. With clinical training experience through Henry Ford Health, Dr. Gill has intentionally expanded beyond traditional clinical medicine into healthcare systems leadership, public health advisory, and municipal strategy.
                </p>
                <p>
                  He brings a rare combination of clinical credibility, federal healthcare experience, and deep community-rooted perspective to every engagement. Committed to driving real impact at the systems level, Dr. Gill advises healthcare organizations, government agencies, and community institutions on strategy, operations, population health, and health equity.
                </p>
              </div>

              {/* Credential pills */}
              <div className="mt-9 flex flex-wrap gap-3">
                {credentials.map((c, i) => (
                  <span key={c} data-testid={`credential-pill-${i}`} className="vs-pill">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    {c}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════ SERVICES ═══════════════ */}
      <section
        id="services"
        data-testid="services-section"
        className="bg-[#130723] py-24 md:py-32 scroll-mt-20"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <Reveal>
            <SectionHead
              eyebrow="What We Do"
              title="Physician-led strategy across healthcare, public health, government, and community."
              subtitle="Six advisory practice areas built on clinical authority, federal healthcare experience, and a deep understanding of the communities our work serves."
            />
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
            {services.map((service, i) => (
              <Reveal key={service.title} delay={i * 80}>
                <article
                  data-testid={`service-card-${i}`}
                  className="vs-card h-full p-8 lg:p-9 flex flex-col"
                >
                  <service.icon className="w-9 h-9 text-[#D4AF37] mb-7" strokeWidth={1.4} />
                  <h3 className="font-display text-xl md:text-2xl font-semibold text-white mb-4 leading-snug">
                    {service.title}
                  </h3>
                  <p className="text-slate-400 text-sm md:text-[15px] leading-relaxed">
                    {service.description}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ WHY VANTARIS ═══════════════ */}
      <section
        id="why-vantaris"
        data-testid="why-vantaris-section"
        className="bg-[#F7F6F2] py-24 md:py-32 scroll-mt-20"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <Reveal>
            <SectionHead
              eyebrow="Why Vantaris"
              title="We don't consult from the outside. We advise from the inside."
              subtitle="With the clinical, operational, and community perspective that most firms simply don't have."
              dark={false}
            />
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12">
            {whyVantaris.map((item, i) => (
              <Reveal key={i} delay={i * 100}>
                <div data-testid={`why-vantaris-item-${i}`} className="flex gap-5">
                  <div className="shrink-0 flex items-center justify-center w-12 h-12 border border-[#D4AF37]/40 bg-white">
                    <item.icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.6} />
                  </div>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl font-semibold text-[#1B0E33] mb-3 leading-snug">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-[15px] leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ EXPERTISE ═══════════════ */}
      <section
        id="expertise"
        data-testid="expertise-section"
        className="bg-[#1B0E33] py-24 md:py-32 scroll-mt-20"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20">
          <Reveal>
            <SectionHead
              eyebrow="Capabilities"
              title="Areas of Expertise."
              subtitle="A practice built across the disciplines that shape population health — from clinical operations to municipal policy."
              align="center"
            />
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-14 flex flex-wrap justify-center gap-3" data-testid="expertise-tags">
              {expertiseTags.map((tag, i) => (
                <span key={tag} data-testid={`expertise-tag-${i}`} className="vs-chip">
                  {tag}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════ INSIGHTS ═══════════════ */}
      <section
        id="insights"
        data-testid="insights-section"
        className="bg-[#130723] py-24 md:py-32 scroll-mt-20"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <Reveal>
            <SectionHead
              eyebrow="Insights"
              title="Perspectives from the intersection."
              subtitle="Healthcare systems, public health, and community strategy — from a physician who operates across all three."
            />
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {insights.length > 0 ? (
              insights.map((article, i) => (
                <Reveal key={article.id} delay={i * 100}>
                  <Link
                    to={`/insights/${article.slug}`}
                    data-testid={`insight-card-${i}`}
                    className="vs-card h-full p-8 lg:p-9 flex flex-col group"
                  >
                    <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#D4AF37] mb-5">
                      {article.category}
                    </p>
                    <h3 className="font-display text-xl md:text-[1.4rem] font-semibold text-white leading-snug mb-4 group-hover:text-[#E5C75B] transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed flex-1 line-clamp-4">
                      {article.excerpt}
                    </p>
                    <span className="mt-7 inline-flex items-center text-[12px] uppercase tracking-[0.14em] text-[#D4AF37] font-semibold">
                      Read More
                      <ArrowUpRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </span>
                  </Link>
                </Reveal>
              ))
            ) : (
              <p className="text-slate-500 col-span-3 text-center">Loading insights...</p>
            )}
          </div>
        </div>
      </section>

      {/* ═══════════════ CONTACT ═══════════════ */}
      <section
        id="contact"
        data-testid="contact-section"
        className="bg-[#1B0E33] py-24 md:py-32 scroll-mt-20"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Heading + contact info */}
            <Reveal className="lg:col-span-5">
              <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#D4AF37] mb-5">
                Contact
              </p>
              <h2 className="font-display text-3xl md:text-4xl lg:text-[2.75rem] font-semibold text-white leading-[1.15] mb-5">
                Let's Build Healthier Systems Together.
              </h2>
              <div className="vs-rule mb-7" />
              <p className="text-slate-300 text-base md:text-lg leading-relaxed mb-10">
                Whether you represent a health system, government agency, community organization, or are exploring a strategic partnership — Vantaris Integrated Solutions is ready to engage.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-white/10">
                    <MapPin className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <span className="text-slate-300 text-sm">Flint, Michigan</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-white/10">
                    <Mail className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <a
                    href="mailto:jgill@vantarisadvisory.com"
                    className="text-slate-300 text-sm hover:text-[#E5C75B] transition-colors"
                    data-testid="contact-email-link"
                  >
                    jgill@vantarisadvisory.com
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-white/10">
                    <Phone className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <a
                    href="tel:+18102284975"
                    className="text-slate-300 text-sm hover:text-[#E5C75B] transition-colors"
                    data-testid="contact-phone-link"
                  >
                    (810) 228-4975
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-white/10">
                    <Linkedin className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <a
                    href="https://linkedin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-300 text-sm hover:text-[#E5C75B] transition-colors"
                    data-testid="contact-linkedin-link"
                  >
                    Connect on LinkedIn
                  </a>
                </div>
              </div>
            </Reveal>

            {/* Form */}
            <Reveal delay={120} className="lg:col-span-7">
              <div className="bg-[#221141] border border-white/5 p-8 md:p-10">
                <ContactForm />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
